import * as React from 'react';
import { defineMessages, useIntl } from 'react-intl';
import { useSelector } from 'react-redux';
import environmentCardPreviewImage from '../../../content/images/wallpaper-environment.jpg';
import { ExtendedIcon } from '../../../icons/initialize-extended-icons';
import { Failure } from '../../../models/common';
import { Tenant } from '../../../models/resource-manager';
import { getSelectedTenant } from '../../../redux/selector/tenant-selector';
import { isNotUndefinedOrWhiteSpace } from '../../../utilities/string';
import { ConfirmationDialogProperties } from '../../common/confirmation-dialog/contexts';
import { FluentIconNames } from '../../common/fluent-icon-names';
import { MetadataItemViewModel } from '../../common/metadata/models';
import { RedeployEnvironmentPanelContextProperties } from '../../redeploy-environment-panel/contexts';
import ResourceCard from '../../resource-card/resource-card';
import { getResourceCardAction } from '../../resource-card/selectors';
import { ChangeEnvironmentExpirationDialogProperties } from '../change-expiration-dialog/contexts';
import { EnvironmentDetailsPanelContextProperties } from '../environment-details-panel/context';
import { EnvironmentViewModel } from '../models';
import { RedeployEnvironmentDialogProperties } from '../redeploy-environment-dialog/contexts';
import {
    EnvironmentTypeMetadata,
    ExpirationDateOffsetMetadata,
    OwnerMetadata,
    ResourceGroupMetadata,
    getShimmeredEnvironmentMetadata,
} from './environment-metadata';
import EnvironmentStateMessage from './environment-state-message';
import EnvironmentStatusImage from './environment-status-image';
import {
    getAreActionsDisabled,
    getEnvironmentBannerViewModel,
    getMetadata,
    getResourceCardError,
    getSecondaryActions,
    getUseTranslucentPreviewImage,
} from './selectors';

interface EnvironmentCardProps {
    environment: EnvironmentViewModel;
    onDeleteSubmitted: (resourceId: string) => void;
    onSeeErrorDetails: (failure: Failure) => void;
    onOpenConfirmationDialog: (properties: ConfirmationDialogProperties) => void;
    onOpenRedeployDialog: (properties: RedeployEnvironmentDialogProperties) => void;
    onOpenRedeployEnvironmentPanel: (properties: RedeployEnvironmentPanelContextProperties) => void;
    onClearRedeployEnvironmentFailure: () => void;
    onOpenChangeEnvironmentExpirationDialog: (properties: ChangeEnvironmentExpirationDialogProperties) => void;
    onOpenEnvironmentDetailsPanel: (props: EnvironmentDetailsPanelContextProperties) => void;
}

interface EnvironmentCardComponentProps extends EnvironmentCardProps {
    selectedTenant?: Tenant;
}

const messages = defineMessages({
    customCommandMenuItemAriaLabel: {
        id: 'EnvironmentCard_CustomCommandMenuItem_AriaLabel',
        defaultMessage: 'Custom commmand on environment',
        description: 'Aria label for "Custom command" contextual menu item in environment card\'s settings button.',
    },
    customCommandMenuItemText: {
        id: 'EnvironmentCard_CustomCommandMenuItem_Text',
        defaultMessage: 'Custom command',
        description: 'Text for "Custom command" contextual menu item in environment card\'s settings button.',
    },
    deleteDialogPrimaryButtonAriaLabel: {
        id: 'EnvironmentCard_DeleteDialogPrimaryButton_AriaLabel',
        defaultMessage: 'Delete the environment',
        description: 'Aria label for the submit button to confirm deleting the environment',
    },
    deleteDialogPrimaryButtonText: {
        id: 'EnvironmentCard_DeleteDialogPrimaryButton_Text',
        defaultMessage: 'Delete',
        description: 'Confirmation button to delete the environment',
    },
    deleteDialogSubText: {
        id: 'EnvironmentCard_DeleteDialogSubText_Text',
        defaultMessage:
            'This environment and all its data will be permanently removed. Are you sure you want to delete {name}?',
        description:
            'Informs the user about the effects of deleting the environment and confirms. {name} should not be localized, it is the name of the environment.',
    },
    deleteDialogTitle: {
        id: 'EnvironmentCard_DeleteDialogTitle_Text',
        defaultMessage: 'Delete environment',
        description: 'Header text for environment delete dialog',
    },
    deleteMenuItemAriaLabel: {
        id: 'EnvironmentCard_DeleteMenuItem_AriaLabel',
        defaultMessage: 'Delete environment',
        description: 'Aria label for "Delete" contextual menu item in environment card\'s settings button.',
    },
    deleteMenuItemText: {
        id: 'EnvironmentCard_DeleteMenuItem_Text',
        defaultMessage: 'Delete',
        description: 'Text for "Delete" contextual menu item in environment card\'s settings button.',
    },
    environmentTypeIconAriaLabel: {
        id: 'EnvironmentCard_EnvironmentTypeIcon_AriaLabel',
        defaultMessage: 'Environment type',
        description: 'Aria label for the environment type icon in the environment card',
    },
    ownerIconAriaLabel: {
        id: 'EnvironmentCard_OwnerIcon_AriaLabel',
        defaultMessage: 'Owner',
        description: 'Aria label for the owner icon in the environment card',
    },
    expirationDateIconAriaLabel: {
        id: 'EnvironmentCard_ExpirationDateIcon_AriaLabel',
        defaultMessage: 'Expiration date',
        description: 'Aria label for the expiration date icon in the environment card',
    },
    redeployMenuItemAriaLabel: {
        id: 'EnvironmentCard_RedeployMenuItem_AriaLabel',
        defaultMessage: 'Redeploy environment',
        description: 'Aria label for "Redeploy" contextual menu item in environment card\'s settings button.',
    },
    redeployMenuItemText: {
        id: 'EnvironmentCard_RedeployMenuItem_Text',
        defaultMessage: 'Redeploy',
        description: 'Text for "Redeploy" contextual menu item in environment card\'s settings button.',
    },
    resourceGroupIconAriaLabel: {
        id: 'EnvironmentCard_ResourceGroupIcon_AriaLabel',
        defaultMessage: 'Resources',
        description: 'Aria label for the resources icon in the environment card',
    },
    redeployDialogPrimaryButtonText: {
        id: 'EnvironmentCard_RedeployDialogPrimaryButton_Text',
        defaultMessage: 'Redeploy',
        description: 'Confirmation button to redeploy the environment',
    },
    redeployDialogSubText: {
        id: 'EnvironmentCard_RedeployDialogSubText_Text',
        defaultMessage:
            'The environment definition {environmentDefinitionName} has no parameters, and will be redeployed immediately.',
        description:
            'Informs the user that their environment will be redeployed. {environmentDefinitionName} should not be localized, it is the name of the environment definition.',
    },
    redeployDialogTitle: {
        id: 'EnvironmentCard_RedeployDialogTitle_Text',
        defaultMessage: 'Redeploy {environmentName}',
        description:
            'Header text for environment redeploy dialog. {environmentName} should not be localized, it is the name of the environment to be redeployed.',
    },
    changeExpirationDateMenuItemAriaLabel: {
        id: 'EnvironmentCard_ChangeExpirationMenuItem_AriaLabel',
        defaultMessage: 'Change expiration',
        description: 'Aria label for "Change expiration" contextual menu item in environment card\'s settings button.',
    },
    changeExpirationDateMenuItemText: {
        id: 'EnvironmentCard_ChangeExpirationMenuItem_Text',
        defaultMessage: 'Change expiration',
        description: 'Text for "Change expiration" contextual menu item in environment card\'s settings button.',
    },
    moreInfoMenuItemAriaLabel: {
        id: 'EnvironmentCard_MoreInfoMenuItem_AriaLabel',
        defaultMessage: 'More info',
        description: 'Aria label for "More info" contextual menu item in environment card\'s settings button.',
    },
    moreInfoMenuItemText: {
        id: 'EnvironmentCard_MoreInfoMenuItem_Text',
        defaultMessage: 'More info',
        description: 'Text for "More info" contextual menu item in environment card\'s settings button.',
    },
});

export const EnvironmentCardComponent: React.FC<EnvironmentCardComponentProps> = React.memo(
    (props: EnvironmentCardComponentProps) => {
        const {
            environment,
            onDeleteSubmitted,
            onSeeErrorDetails,
            selectedTenant,
            onOpenConfirmationDialog,
            onOpenRedeployEnvironmentPanel,
            onClearRedeployEnvironmentFailure,
            onOpenRedeployDialog,
            onOpenChangeEnvironmentExpirationDialog,
            onOpenEnvironmentDetailsPanel,
        } = props;

        const {
            resource,
            state,
            projectName: projectResourceName,
            projectDisplayName,
            ownerDisplayName,
            environmentDefinition,
            ownerIsReady,
            isCardContentReady,
            failureOnEnvironment,
            devCenterName,
            expirationDateOffset,
        } = environment;
        const { failure } = failureOnEnvironment ?? {};
        const { catalogName, environmentDefinitionName, environmentType, expirationDate, name, resourceGroupId, uri } =
            resource;
        const environmentDefinitionParameters = environmentDefinition?.parameters ?? [];

        // Intl hooks
        const { formatMessage } = useIntl();

        // Note: putting these memoizers here since they're used within callbacks
        const resourceCardError = React.useMemo(
            () => getResourceCardError(failureOnEnvironment, state),
            [failureOnEnvironment, state]
        );

        const hasFailure = !!failure;

        const isEnvironmentDefinitionReady = !!environmentDefinition;
        const isShimmered = !isCardContentReady;

        // Callback hooks
        const onDeleteDialogPrimaryButtonClicked = React.useCallback(() => {
            onDeleteSubmitted(uri);
        }, [onDeleteSubmitted, uri]);

        const stateElement = <EnvironmentStateMessage state={state} />;
        const statusImageElement = <EnvironmentStatusImage name={name} state={state} />;

        const onSeeErrorDetailsClicked = React.useCallback(() => {
            if (!hasFailure) {
                return;
            }

            onSeeErrorDetails(failure);
        }, [hasFailure, failure, onSeeErrorDetails]);

        // Memoized data
        const areActionsDisabled = React.useMemo(() => getAreActionsDisabled(state), [state]);
        const deleteDialogSubTextValues = React.useMemo(() => ({ name }), [name]);
        const useTranslucentPreviewImage = React.useMemo(() => getUseTranslucentPreviewImage(state), [state]);
        const hasParameters = environmentDefinitionParameters.length > 0;

        const projectName = React.useMemo(
            () => (isNotUndefinedOrWhiteSpace(projectDisplayName) ? projectDisplayName : projectResourceName),
            [projectDisplayName, projectResourceName]
        );

        const onDeleteMenuItemClicked = React.useCallback(() => {
            onOpenConfirmationDialog({
                onPrimaryButtonSubmitted: onDeleteDialogPrimaryButtonClicked,
                primaryButtonAriaLabel: formatMessage(messages.deleteDialogPrimaryButtonAriaLabel),
                primaryButtonText: formatMessage(messages.deleteDialogPrimaryButtonText),
                subText: formatMessage(messages.deleteDialogSubText, deleteDialogSubTextValues),
                title: formatMessage(messages.deleteDialogTitle),
            });
        }, [onOpenConfirmationDialog, onDeleteDialogPrimaryButtonClicked, deleteDialogSubTextValues, formatMessage]);

        // Resource card actions
        const deleteMenuItem = React.useMemo(
            () =>
                getResourceCardAction(
                    formatMessage(messages.deleteMenuItemAriaLabel),
                    'Delete',
                    'delete',
                    onDeleteMenuItemClicked,
                    formatMessage(messages.deleteMenuItemText)
                ),
            [formatMessage, onDeleteMenuItemClicked]
        );

        const onOpenRedeployConfirmationDialog = React.useCallback(() => {
            onOpenRedeployDialog({
                environment: resource,
            });
        }, [onOpenRedeployDialog, resource]);

        const onRedeployMenuItemClicked = React.useCallback(() => {
            onClearRedeployEnvironmentFailure();

            if (hasParameters) {
                onOpenRedeployEnvironmentPanel({ environment });
            } else {
                onOpenRedeployConfirmationDialog();
            }
        }, [
            onClearRedeployEnvironmentFailure,
            hasParameters,
            onOpenRedeployEnvironmentPanel,
            onOpenRedeployConfirmationDialog,
            environment,
        ]);

        const redeployMenuItem = React.useMemo(
            () =>
                getResourceCardAction(
                    formatMessage(messages.redeployMenuItemAriaLabel),
                    'Redeploy',
                    'deploy',
                    onRedeployMenuItemClicked,
                    formatMessage(messages.redeployMenuItemText)
                ),
            [formatMessage, onRedeployMenuItemClicked]
        );

        const onChangeEnvironmentExpirationMenuItemItemClicked = React.useCallback(() => {
            onOpenChangeEnvironmentExpirationDialog({ environment: resource });
        }, [onOpenChangeEnvironmentExpirationDialog, resource]);

        const changeExpirationMenuItem = React.useMemo(
            () =>
                getResourceCardAction(
                    formatMessage(messages.changeExpirationDateMenuItemAriaLabel),
                    'Calendar',
                    'changeExpiration',
                    onChangeEnvironmentExpirationMenuItemItemClicked,
                    formatMessage(messages.changeExpirationDateMenuItemText)
                ),
            [formatMessage, onChangeEnvironmentExpirationMenuItemItemClicked]
        );

        const onMoreInfoMenuItemClicked = React.useCallback(() => {
            onOpenEnvironmentDetailsPanel({
                devCenterName,
                projectName,
                expirationDate,
                catalogName,
                environmentDefinitionName,
                name,
            });
        }, [
            onOpenEnvironmentDetailsPanel,
            expirationDate,
            devCenterName,
            projectName,
            catalogName,
            environmentDefinitionName,
            name,
        ]);

        const moreInfoMenuItem = React.useMemo(
            () =>
                getResourceCardAction(
                    formatMessage(messages.moreInfoMenuItemAriaLabel),
                    'Info',
                    'moreInfo',
                    onMoreInfoMenuItemClicked,
                    formatMessage(messages.moreInfoMenuItemAriaLabel)
                ),
            [formatMessage, onMoreInfoMenuItemClicked]
        );

        const secondaryActions = React.useMemo(
            () =>
                getSecondaryActions(
                    deleteMenuItem,
                    redeployMenuItem,
                    changeExpirationMenuItem,
                    state,
                    isEnvironmentDefinitionReady,
                    moreInfoMenuItem
                ),
            [
                deleteMenuItem,
                redeployMenuItem,
                changeExpirationMenuItem,
                state,
                isEnvironmentDefinitionReady,
                moreInfoMenuItem,
            ]
        );

        // Resource card metadata
        const environmentTypeMetadata: MetadataItemViewModel = React.useMemo(
            () => ({
                description: formatMessage(messages.environmentTypeIconAriaLabel),
                icon: ExtendedIcon.WebEnvironment,
                key: 'environment-type',
                primary: false,
                value: <EnvironmentTypeMetadata environmentType={environmentType} />,
            }),
            [environmentType, formatMessage]
        );

        const ownerMetadata: MetadataItemViewModel = React.useMemo(
            () => ({
                description: formatMessage(messages.ownerIconAriaLabel),
                icon: 'Contact',
                key: 'owner',
                primary: false,
                value: <OwnerMetadata ownerDisplayName={ownerDisplayName} ownerIsReady={ownerIsReady} />,
            }),
            [ownerDisplayName, ownerIsReady, formatMessage]
        );

        const expirationDateMetadata: MetadataItemViewModel | undefined = React.useMemo(
            () =>
                !!expirationDateOffset?.offset && !!expirationDateOffset?.unit
                    ? {
                          description: formatMessage(messages.expirationDateIconAriaLabel),
                          icon: FluentIconNames.Calendar,
                          key: 'expiration-date',
                          primary: false,
                          value: <ExpirationDateOffsetMetadata expirationDateOffset={expirationDateOffset} />,
                      }
                    : undefined,
            [expirationDateOffset, formatMessage]
        );

        const resourceGroupMetadata: MetadataItemViewModel | undefined = React.useMemo(() => {
            if (!resourceGroupId) {
                return undefined;
            }

            return {
                description: formatMessage(messages.resourceGroupIconAriaLabel),
                icon: 'NavigateExternalInline',
                key: 'resource-group',
                primary: true,
                value: (
                    <ResourceGroupMetadata
                        resourceGroupId={resourceGroupId}
                        tenantDomain={selectedTenant?.defaultDomain}
                        environmentName={name}
                    />
                ),
            };
        }, [resourceGroupId, selectedTenant?.defaultDomain, name, formatMessage]);

        const metadata = React.useMemo(
            () =>
                getMetadata(
                    environmentTypeMetadata,
                    ownerMetadata,
                    resourceGroupMetadata,
                    expirationDateMetadata,
                    state
                ),
            [environmentTypeMetadata, ownerMetadata, resourceGroupMetadata, expirationDateMetadata, state]
        );

        const hasResourceGroup = !!resourceGroupId;
        const shimmeredEnvironmentMetadata = React.useMemo(
            () => getShimmeredEnvironmentMetadata(hasResourceGroup),
            [hasResourceGroup]
        );

        const cardBanner = React.useMemo(
            () => getEnvironmentBannerViewModel(resourceCardError, hasFailure ? onSeeErrorDetailsClicked : undefined),
            [resourceCardError, onSeeErrorDetailsClicked, hasFailure]
        );

        return (
            <ResourceCard
                areSecondaryActionsDisabled={areActionsDisabled}
                metadata={metadata}
                name={name}
                stateElement={stateElement}
                statusImageElement={statusImageElement}
                previewImageSrc={environmentCardPreviewImage}
                projectName={projectName}
                secondaryActions={secondaryActions}
                useTranslucentPreviewImage={useTranslucentPreviewImage}
                displayStateAtTheBottom
                isShimmered={isShimmered}
                shimmeredMetadata={shimmeredEnvironmentMetadata}
                cardBanner={cardBanner}
            />
        );
    }
);

export const EnvironmentCardContainer: React.FC<EnvironmentCardProps> = (props: EnvironmentCardProps) => {
    // Application state hooks
    const selectedTenant = useSelector(getSelectedTenant);

    return <EnvironmentCardComponent {...props} selectedTenant={selectedTenant} />;
};

export default EnvironmentCardContainer;
